import { getCurrentUrl } from '@pixum/auth-core-ui';
import LogoutIcon from '@pixum/svg-icon/lib/Custom/Logout';
import LoggedInIcon from '@pixum/svg-icon/lib/Custom/LoggedIn';
import { type LinkSkeletonNode } from 'data-layer';
import type { UserPersonalInfo } from 'hooks';
import { omit, sendDimension3Tracking, trackEvent, TRACKING_CATEGORIES } from 'utils';
import { LINK_NODE_INTERNAL_NAME } from 'components/constants';
import type { PopupItemProps } from '@pixum/web-header';

/**
 * Event handler for menu item clicks. Function tracks the events and handles the logout scenario.
 *
 * @param title - The title of the menu item clicked.
 * @param internalName - The internal name of the menu item, used to identify specific actions like logout.
 */
function onMenuItemClick(title: string, internalName: string): void {
  const currentUrl = getCurrentUrl();
  trackEvent(TRACKING_CATEGORIES.HEADER, title, currentUrl);

  // Add special dimension3 tracking for logout menu item
  if (internalName === LINK_NODE_INTERNAL_NAME.Logout) {
    sendDimension3Tracking(TRACKING_CATEGORIES.HEADER, title, currentUrl, internalName);
  }
}

/**
 * Transforms the header menu items by mapping over the provided menu items
 * and adding additional properties or modifying existing ones based on
 * specific conditions.
 *
 * @param {LinkSkeletonNode[]} menuItems - The array of menu items to be transformed.
 * @param {UserPersonalInfo} userInfo - The user's personal information, including email.
 * @returns {Record<string, unknown>[]} - The transformed menu items with additional properties.
 *
 * Each menu item is enhanced with:
 * - `alignCenter`: A boolean indicating whether the item should be center-aligned.
 * - `onClick`: A function to handle click events and track the event.
 * - `icon`: An optional icon for the menu item (e.g., LogoutIcon for logout items).
 * - `subIcon`: An optional secondary icon for the menu item (e.g., LoggedInIcon for logout items).
 * - `subText`: An optional subtext, such as the user's email for logout items.
 * - `href`: The URL of the menu item.
 * - `text`: The display text of the menu item.
 * - `title`: The title of the menu item.
 *
 * Special handling is applied for items with an `internalName` of `LINK_NODE_INTERNAL_NAME.Logout`:
 * - The item is center-aligned.
 * - A `LogoutIcon` is added as the main icon.
 * - A `LoggedInIcon` is added as a secondary icon.
 * - The user's email is displayed as subtext.
 */
export function transformHeaderMenuItems(menuItems: LinkSkeletonNode[], userInfo: UserPersonalInfo): PopupItemProps[] {
  const { email } = userInfo;

  return menuItems.map(item => {
    const rest = omit(item, ['cmsType', 'sys']);

    const subItem: PopupItemProps = {
      ...rest,
      icon: item.internalName === LINK_NODE_INTERNAL_NAME.Logout ? <LogoutIcon /> : undefined,
      subIcon: item.internalName === LINK_NODE_INTERNAL_NAME.Logout ? <LoggedInIcon /> : undefined,
      subText: item.internalName === LINK_NODE_INTERNAL_NAME.Logout ? email : undefined,
      alignCenter: item.internalName === LINK_NODE_INTERNAL_NAME.Logout,
      onClick: () => onMenuItemClick(item.title, item.internalName)
    };

    subItem.href = item.url;
    subItem.text = item.title;
    subItem.title = item.title;

    return subItem;
  });
}
